import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { TaskDetailDrawer } from "@/components/tasks/task-detail-drawer";
import {
  RefreshCw,
  Zap,
  Clock,
  CheckCircle2,
  XCircle,
  ListTodo,
  Hash,
  Mail,
  FileText,
} from "lucide-react";

const CRON_PRESETS: Record<string, string> = {
  "0 * * * *": "Every hour",
  "0 0 * * *": "Daily at midnight",
  "0 9 * * *": "Daily at 09:00",
  "0 9 * * 1-5": "Weekdays at 09:00",
  "0 17 * * 1-5": "Weekdays at 17:00",
  "0 0 * * 1": "Weekly (Monday)",
  "0 0 1 * *": "Monthly (1st)",
};

function cronToText(expr: string | null | unknown): string {
  if (!expr) return "—";
  // Guard against object values stored before normalisation fix
  if (typeof expr === "object") {
    const obj = expr as Record<string, unknown>;
    const val = obj.expression ?? obj.cron ?? obj.schedule ?? obj.value ?? obj.every;
    if (typeof val === "string") return CRON_PRESETS[val] ?? val;
    return "—";
  }
  if (typeof expr !== "string") return "—";
  if (expr === "[object Object]") return "—";
  if (CRON_PRESETS[expr]) return CRON_PRESETS[expr];
  // Try to parse common patterns
  const parts = expr.split(" ");
  if (parts.length === 5) {
    const [min, hr, dom, mon, dow] = parts;
    if (dom === "*" && mon === "*" && min !== "*" && hr !== "*") {
      const time = `${hr!.padStart(2, "0")}:${min!.padStart(2, "0")}`;
      if (dow === "*") return `Daily at ${time}`;
      if (dow === "1-5") return `Weekdays at ${time}`;
      if (dow === "0-6" || dow === "*") return `Daily at ${time}`;
    }
  }
  return expr;
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) {
    const abs = -diff;
    if (abs < 3_600_000) return `in ${Math.round(abs / 60_000)}m`;
    if (abs < 86_400_000) return `in ${Math.round(abs / 3_600_000)}h`;
    return `in ${Math.round(abs / 86_400_000)}d`;
  }
  if (diff < 60_000) return "<1m ago";
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  paused: "warning",
  completed: "secondary",
  failed: "danger",
};

type Filter = "all" | "recurring" | "one_off";

/** Describe where a task's output will land. */
function describeDestination(task: AgentTask): { icon: typeof Hash; label: string } {
  if (task.announceChannelName) {
    return { icon: Hash, label: task.announceChannelName };
  }
  const prompt = (task.taskPrompt || "").toLowerCase();
  if (prompt.includes("email") || prompt.includes(" mail ") || prompt.includes("send to")) {
    return { icon: Mail, label: "via agent email" };
  }
  return { icon: FileText, label: "Task Reports" };
}

interface TasksProps {
  /** When set, the agent dropdown is hidden and the list is locked to this agent. */
  lockedAgentId?: string;
  /** Skip the CenterPanel wrapper — for embedding inside another layout (e.g. workspace tab). */
  embedded?: boolean;
}

export function Tasks({ lockedAgentId, embedded = false }: TasksProps = {}) {
  const { tenantId } = useTenant();
  const [filter, setFilter] = useState<Filter>("all");
  const [agentFilter, setAgentFilter] = useState<string>("");
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);

  const effectiveAgentId = lockedAgentId ?? agentFilter;

  const { data: tasks = [], isLoading } = useQuery({
    // Include the filter in the queryKey so React Query re-fetches when the
    // dropdown changes. Previously the key was static, so server-side
    // filtering was wired but never triggered.
    queryKey: [...queryKeys.agentTasks.byCompany(tenantId), { agentId: effectiveAgentId || null }],
    queryFn: () =>
      agentTasksApi.list(
        tenantId,
        effectiveAgentId ? { agentId: effectiveAgentId } : undefined,
      ),
    enabled: !!tenantId && (!lockedAgentId || !!effectiveAgentId),
  });

  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.type === filter);

  const body = (
    <>
      {/* Filters */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {(
            [
              ["all", "All"],
              ["recurring", "Recurring"],
              ["one_off", "One-off"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                filter === key
                  ? "bg-accent/10 text-accent"
                  : "text-text-muted hover:text-text-primary bg-surface",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {!lockedAgentId && (
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs"
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        )}

        <span className="text-xs text-text-muted ml-auto">
          {filtered.length} task{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <p className="text-sm text-text-muted py-8 text-center">Loading tasks...</p>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <ListTodo size={32} className="text-text-muted opacity-40" />
          <p className="text-sm text-text-muted">No tasks found</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-x-auto">
          {/* Header — keeps a min-width so the column structure stays intact;
              users on phones can swipe horizontally within the table. */}
          <div className="grid min-w-[760px] grid-cols-[1fr_110px_130px_130px_90px_95px_95px] gap-2 px-4 py-2 bg-surface text-[11px] font-medium text-text-muted border-b border-border">
            <span>Task</span>
            <span>Agent</span>
            <span>Schedule</span>
            <span>Destination</span>
            <span>Status</span>
            <span>Last Run</span>
            <span>Next Run</span>
          </div>

          {/* Rows */}
          {filtered.map((task) => {
            const dest = describeDestination(task);
            const DestIcon = dest.icon;
            return (
              <div
                key={task.id}
                onClick={() => setSelectedTask(task)}
                className="grid min-w-[760px] grid-cols-[1fr_110px_130px_130px_90px_95px_95px] gap-2 px-4 py-3 border-b border-border hover:bg-surface-hover cursor-pointer transition-colors items-center"
              >
                {/* Title */}
                <div className="flex items-center gap-2 min-w-0">
                  {task.type === "recurring" ? (
                    <RefreshCw size={12} className="text-purple-400 shrink-0" />
                  ) : (
                    <Zap size={12} className="text-blue-400 shrink-0" />
                  )}
                  <span className="text-sm text-text-primary truncate">{task.title}</span>
                  {task.createdBy === "agent" && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 shrink-0">
                      agent
                    </Badge>
                  )}
                </div>

                {/* Agent */}
                <span className="text-xs text-text-secondary truncate">
                  {task.agentName ?? "—"}
                </span>

                {/* Schedule */}
                <span className="text-xs text-text-muted truncate" title={task.cronExpression || ""}>
                  {task.type === "recurring" ? (
                    <>
                      <Clock size={10} className="inline mr-1" />
                      {cronToText(task.cronExpression)}
                    </>
                  ) : (
                    "—"
                  )}
                </span>

                {/* Destination */}
                <span className="text-xs text-text-muted truncate flex items-center gap-1" title={dest.label}>
                  <DestIcon size={10} className="shrink-0" />
                  <span className="truncate">{dest.label}</span>
                </span>

                {/* Status */}
                <Badge
                  variant={statusVariant[task.status] ?? "default"}
                  className="text-[10px] w-fit"
                >
                  {task.status}
                </Badge>

                {/* Last run */}
                <div className="flex items-center gap-1">
                  {task.lastResult?.success === true ? (
                    <CheckCircle2 size={10} className="text-emerald-400" />
                  ) : task.lastResult?.success === false ? (
                    <XCircle size={10} className="text-red-400" />
                  ) : null}
                  <span className="text-xs text-text-muted">
                    {relativeTime(task.lastRunAt)}
                  </span>
                </div>

                {/* Next run */}
                <span className="text-xs text-text-muted">
                  {task.type === "recurring" && task.enabled
                    ? relativeTime(task.nextRun)
                    : "—"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail / edit drawer */}
      <TaskDetailDrawer
        task={selectedTask}
        tenantId={tenantId}
        onClose={() => setSelectedTask(null)}
      />
    </>
  );

  if (embedded) {
    return <div className="h-full overflow-y-auto px-6 py-4">{body}</div>;
  }
  return <CenterPanel title="Tasks">{body}</CenterPanel>;
}
